/**
 * ═══════════════════════════════════════════════════════════════
 * WASP Frontend v2.0 - Conversations List Component
 * ═══════════════════════════════════════════════════════════════
 * 
 * @file components/ConversationsList.tsx
 * @version 1.0.0
 * @description Lista de conversaciones con filtros, búsqueda y paginación
 * @created 2025-10-30 - Fase 2 Session 1
 * 
 * FEATURES:
 * - Lista de conversaciones estilo BulkSend
 * - Búsqueda en tiempo real
 * - Filtros por estado (active/pending/closed)
 * - Ordenamiento configurable
 * - Paginación
 * - Estados visuales con badges
 * - Avatares con iniciales
 * - Timestamps relativos
 * - Click para seleccionar conversación
 * - Indicador de mensajes no leídos
 * - Responsive design
 */

import React, { useState, useMemo } from 'react';
import { Card, Form, InputGroup, Table, Badge, Pagination, Row, Col, Button, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faUser,
  faCircle,
  faClock,
  faArrowUp,
  faArrowDown,
  faFilter,
  faInbox,
  faCheckCircle,
  faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';

// Types
import {
  ConversationsListProps,
  ConversationItem,
  FilterOptions
} from '../types/performance_types';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Obtiene las iniciales de un nombre
 */
const getInitials = (name: string): string => {
  if (!name) return '??';
  
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Genera un color basado en el nombre
 */
const getColorFromName = (name: string): string => {
  const colors = [
    '#0d6efd', // blue
    '#6610f2', // indigo
    '#6f42c1', // purple
    '#d63384', // pink
    '#dc3545', // red
    '#fd7e14', // orange
    '#ffc107', // yellow
    '#198754', // green
    '#20c997', // teal
    '#0dcaf0'  // cyan
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Formatea timestamp a formato relativo
 */
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (seconds < 60) return 'Ahora';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' });
};

/**
 * Obtiene el icono según el estado
 */
const getStatusIcon = (status: 'active' | 'pending' | 'closed') => {
  switch (status) {
    case 'active':
      return faCheckCircle;
    case 'pending':
      return faHourglassHalf;
    case 'closed':
      return faInbox;
    default:
      return faCircle;
  }
};

/**
 * Obtiene el color según el estado
 */
const getStatusColor = (status: 'active' | 'pending' | 'closed'): string => {
  switch (status) {
    case 'active':
      return '#198754'; // green
    case 'pending':
      return '#ffc107'; // yellow
    case 'closed':
      return '#6c757d'; // gray
    default:
      return '#6c757d';
  }
};

/**
 * Obtiene el label según el estado
 */
const getStatusLabel = (status: 'active' | 'pending' | 'closed'): string => {
  switch (status) {
    case 'active':
      return 'Activa';
    case 'pending':
      return 'Pendiente';
    case 'closed':
      return 'Cerrada';
    default:
      return status;
  }
};

// ═══════════════════════════════════════════════════════════════
// CONVERSATIONS LIST COMPONENT
// ═══════════════════════════════════════════════════════════════

const ConversationsList: React.FC<ConversationsListProps> = ({
  conversations,
  total,
  page,
  pageSize,
  onPageChange,
  onSelectConversation,
  filters,
  onFilterChange,
  loading = false
}) => {

  // ─────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────

  const [searchQuery, setSearchQuery] = useState<string>(filters.searchQuery || '');
  const [selectedStatus, setSelectedStatus] = useState<string[]>(
    filters.conversationStatus || []
  );

  // ─────────────────────────────────────────────────────────────
  // COMPUTED
  // ─────────────────────────────────────────────────────────────

  /**
   * Filtra conversaciones por búsqueda y estado
   */
  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conv => 
        conv.contactName.toLowerCase().includes(query) ||
        conv.phoneNumber.includes(query) ||
        conv.lastMessage.toLowerCase().includes(query)
      );
    }

    // Filtrar por estado
    if (selectedStatus.length > 0) {
      filtered = filtered.filter(conv => 
        selectedStatus.includes(conv.status)
      );
    }

    return filtered;
  }, [conversations, searchQuery, selectedStatus]);

  /**
   * Calcula páginas totales
   */
  const totalPages = Math.ceil(filteredConversations.length / pageSize);

  /**
   * Conversaciones de la página actual
   */
  const paginatedConversations = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredConversations.slice(startIndex, endIndex);
  }, [filteredConversations, page, pageSize]);

  // ─────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────

  /**
   * Maneja cambio en búsqueda
   */
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    onFilterChange({
      ...filters,
      searchQuery: query,
      page: 1 // Reset a página 1
    });
  };

  /**
   * Maneja toggle de filtro de estado
   */
  const handleStatusToggle = (status: 'active' | 'pending' | 'closed') => {
    let newStatus: string[];
    
    if (selectedStatus.includes(status)) {
      newStatus = selectedStatus.filter(s => s !== status);
    } else {
      newStatus = [...selectedStatus, status];
    }
    
    setSelectedStatus(newStatus);
    
    onFilterChange({
      ...filters,
      conversationStatus: newStatus.length > 0 ? newStatus as ('active' | 'pending' | 'closed')[] : undefined,
      page: 1
    });
  };

  /**
   * Maneja click en conversación
   */
  const handleConversationClick = (conversation: ConversationItem) => {
    if (onSelectConversation) {
      onSelectConversation(conversation);
    }
  };

  /**
   * Maneja cambio de página
   */
  const handlePageChange = (newPage: number) => {
    onPageChange(newPage);
    onFilterChange({
      ...filters,
      page: newPage
    });
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  return (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)'
    }}>
      <Card.Body className="p-4">
        
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <h5 className="mb-1" style={{ fontWeight: '600' }}>
              <FontAwesomeIcon icon={faInbox} className="me-2" style={{ color: '#0d6efd' }} />
              Conversaciones Recientes
            </h5>
            <small className="text-muted">
              {filteredConversations.length} de {total} conversaciones
            </small>
          </Col>
        </Row>

        {/* Filtros y Búsqueda */}
        <Row className="mb-3">
          <Col md={8}>
            <InputGroup>
              <InputGroup.Text style={{ 
                backgroundColor: 'var(--bs-body-bg)',
                border: '1px solid var(--bs-border-color)'
              }}>
                <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--bs-secondary)' }} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre, teléfono o mensaje..."
                value={searchQuery}
                onChange={handleSearchChange}
                style={{
                  border: '1px solid var(--bs-border-color)',
                  backgroundColor: 'var(--bs-body-bg)'
                }}
              />
            </InputGroup>
          </Col>
          <Col md={4}>
            <ButtonGroup size="sm" className="w-100">
              <Button
                variant={selectedStatus.includes('active') ? 'success' : 'outline-success'}
                onClick={() => handleStatusToggle('active')}
                style={{ fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Activas
              </Button>
              <Button
                variant={selectedStatus.includes('pending') ? 'warning' : 'outline-warning'}
                onClick={() => handleStatusToggle('pending')}
                style={{ fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faHourglassHalf} className="me-1" />
                Pendientes
              </Button>
              <Button
                variant={selectedStatus.includes('closed') ? 'secondary' : 'outline-secondary'}
                onClick={() => handleStatusToggle('closed')}
                style={{ fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faInbox} className="me-1" />
                Cerradas
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        {/* Tabla de Conversaciones */}
        <div style={{ 
          overflowX: 'auto',
          borderRadius: '8px',
          border: '1px solid var(--bs-border-color)'
        }}>
          <Table hover responsive className="mb-0" style={{ minWidth: '700px' }}>
            <thead style={{ 
              backgroundColor: 'var(--bs-light)',
              borderBottom: '2px solid var(--bs-border-color)'
            }}>
              <tr>
                <th style={{ 
                  padding: '1rem',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: 'var(--bs-secondary)'
                }}>
                  Contacto
                </th>
                <th style={{ 
                  padding: '1rem',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: 'var(--bs-secondary)'
                }}>
                  Último Mensaje
                </th>
                <th style={{ 
                  padding: '1rem',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: 'var(--bs-secondary)',
                  textAlign: 'center'
                }}>
                  Estado
                </th>
                <th style={{ 
                  padding: '1rem',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: 'var(--bs-secondary)',
                  textAlign: 'center'
                }}>
                  Mensajes
                </th>
                <th style={{ 
                  padding: '1rem',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: 'var(--bs-secondary)',
                  textAlign: 'right'
                }}>
                  Última Act.
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedConversations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <FontAwesomeIcon icon={faInbox} size="3x" style={{ color: 'var(--bs-secondary)', opacity: 0.3 }} className="mb-3" />
                    <p className="text-muted mb-0">No se encontraron conversaciones</p>
                    <small className="text-muted">Intenta ajustar los filtros de búsqueda</small>
                  </td>
                </tr>
              ) : (
                paginatedConversations.map((conversation) => (
                  <tr 
                    key={conversation.id}
                    onClick={() => handleConversationClick(conversation)}
                    style={{ 
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bs-light)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}>
                    
                    {/* Contacto */}
                    <td style={{ padding: '1rem' }}>
                      <div className="d-flex align-items-center">
                        {/* Avatar */}
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: getColorFromName(conversation.contactName),
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            marginRight: '0.75rem',
                            flexShrink: 0
                          }}>
                          {conversation.avatarUrl ? (
                            <img 
                              src={conversation.avatarUrl} 
                              alt={conversation.contactName}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                borderRadius: '50%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            getInitials(conversation.contactName)
                          )}
                        </div>
                        
                        {/* Info */}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: '600',
                            color: 'var(--bs-body-color)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {conversation.contactName}
                          </div>
                          <small style={{ 
                            color: 'var(--bs-secondary)',
                            fontSize: '0.75rem'
                          }}>
                            {conversation.phoneNumber}
                          </small>
                        </div>

                        {/* Badge de no leídos */}
                        {conversation.unreadCount > 0 && (
                          <Badge 
                            bg="danger"
                            pill
                            style={{
                              marginLeft: '0.5rem',
                              fontSize: '0.7rem'
                            }}>
                            {conversation.unreadCount}
                          </Badge>
                        )}
                      </div>
                    </td>

                    {/* Último Mensaje */}
                    <td style={{ padding: '1rem' }}>
                      <div style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '300px',
                        color: 'var(--bs-secondary)',
                        fontSize: '0.875rem'
                      }}>
                        {conversation.lastMessageDirection === 'outgoing' && (
                          <FontAwesomeIcon 
                            icon={faArrowUp} 
                            size="xs"
                            className="me-1"
                            style={{ color: '#0d6efd' }}
                          />
                        )}
                        {conversation.lastMessageDirection === 'incoming' && (
                          <FontAwesomeIcon 
                            icon={faArrowDown} 
                            size="xs"
                            className="me-1"
                            style={{ color: '#198754' }}
                          />
                        )}
                        {conversation.lastMessage}
                      </div>
                    </td>

                    {/* Estado */}
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <Badge 
                        bg="light"
                        text="dark"
                        style={{
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          border: `1px solid ${getStatusColor(conversation.status)}40`,
                          color: getStatusColor(conversation.status)
                        }}>
                        <FontAwesomeIcon 
                          icon={getStatusIcon(conversation.status)} 
                          size="xs"
                          className="me-1"
                        />
                        {getStatusLabel(conversation.status)}
                      </Badge>
                    </td>

                    {/* Contador de mensajes */}
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{ 
                        fontWeight: '600',
                        color: 'var(--bs-body-color)'
                      }}>
                        {conversation.messageCount}
                      </span>
                    </td>

                    {/* Última actualización */}
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <small style={{ 
                        color: 'var(--bs-secondary)',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {formatRelativeTime(conversation.lastMessageTime)}
                      </small>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <Row className="mt-3">
            <Col className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Mostrando {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, filteredConversations.length)} de {filteredConversations.length}
              </small>
              
              <Pagination className="mb-0">
                <Pagination.First 
                  onClick={() => handlePageChange(1)}
                  disabled={page === 1}
                />
                <Pagination.Prev 
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                />
                
                {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                  let pageNum = page - 2 + idx;
                  if (pageNum < 1) pageNum = idx + 1;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === page}
                      onClick={() => handlePageChange(pageNum)}>
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                />
                <Pagination.Last 
                  onClick={() => handlePageChange(totalPages)}
                  disabled={page === totalPages}
                />
              </Pagination>
            </Col>
          </Row>
        )}

      </Card.Body>
    </Card>
  );
};

export default ConversationsList;